import type React from "react";
import {useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import type {DashboardItem} from "./services/dashboardsApi";
import {notifyDashboardError, useRenameDashboardMutation} from "./services/dashboardsMutations";

type Props = {
    /** The dashboard being renamed; null keeps the dialog closed. */
    target: DashboardItem | null;
    /** The databag whose "Your dashboards" list shows the new title. */
    databagId: number;
    onClose: () => void;
};

/** Rename one of your dashboards; closes once the rename lands. */
export const RenameDashboardDialog: React.FC<Props> = ({target, databagId, onClose}) => {
    const {mutate: renameDashboard, isPending: isRenaming} = useRenameDashboardMutation(databagId);
    const [value, setValue] = useState(target?.title ?? "");

    // Each opening starts from the dashboard's title; closing keeps the text while the dialog fades out.
    const [prevTarget, setPrevTarget] = useState(target);
    if (prevTarget !== target) {
        setPrevTarget(target);
        if (target) setValue(target.title);
    }

    const submit = () => {
        const next = value.trim();
        if (!target || target.dashgenId === null) return;
        if (!next || next === target.title) {
            onClose();
            return;
        }
        renameDashboard({dashboardId: target.dashgenId, title: next}, {
            onSuccess: onClose,
            onError: () => notifyDashboardError("rename", target.id),
        });
    };

    return (
        <Dialog className="dsh-portal" open={target !== null} onClose={() => !isRenaming && onClose()} maxWidth="xs" fullWidth>
            <DialogTitle>Rename dashboard</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    margin="dense"
                    value={value}
                    disabled={isRenaming}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") submit();
                    }}
                    slotProps={{htmlInput: {"aria-label": "Dashboard name", maxLength: 250}}}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRenaming}>Cancel</Button>
                <Button variant="contained" className="dsh-btn-primary" onClick={submit} disabled={isRenaming || !value.trim()}>
                    Rename
                </Button>
            </DialogActions>
        </Dialog>
    );
};
